import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Snackbar,
  Divider,
  Autocomplete,
  TextField,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import lambdaFetch from '../functions/FetchFromLambda';
import ReactTable from './ReactTable';
import { useOpenSearchLoadsTableCols } from '../hooks/OpenSearchLoadsCols';
import { smallScreenWidth } from '../constants/AppConstants';

/** Extract raw items array from dynamo/opensearch-loads API response */
function parseRunsResponse(resp) {
  if (!resp?.success || !resp?.data) return [];
  if (Array.isArray(resp.data)) return resp.data;
  if (resp.data?.body) {
    const bodyObj = typeof resp.data.body === 'string' ? JSON.parse(resp.data.body) : resp.data.body;
    return Array.isArray(bodyObj?.data) ? bodyObj.data : bodyObj?.data ? [bodyObj.data] : [];
  }
  if (Array.isArray(resp.data?.data)) return resp.data.data;
  if (resp.data?.data) return [resp.data.data];
  return [];
}

/** Normalize a single run row (handles DynamoDB/API casing) */
function normalizeRunRow(row) {
  return {
    executionId: row.executionId ?? row.ExecutionId ?? row.execution_id,
    folders: row.folders ?? row.Folders ?? [],
    loadAll: row.loadAll ?? row.load_all ?? false,
    status: (row.status ?? row.STATUS ?? row.Status ?? 'pending').toString(),
    username: row.username ?? row.Username ?? row.userName,
    createdAt: row.createdAt ?? row.CreatedAt ?? row.created_at,
    completedAt: row.completedAt ?? row.CompletedAt ?? row.completed_at,
    error: row.error ?? row.Error,
    output: row.output ?? row.Output,
    ...row,
  };
}

/** Process raw items into sorted runs with display fields */
function processRunsData(items) {
  return items
    .map((row) => {
      const n = normalizeRunRow(row);
      return {
        ...n,
        foldersDisplay: n.loadAll ? 'All' : (n.folders || []).join(', ') || '—',
      };
    })
    .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
}

/** Return true if status indicates run is still in progress */
function isPendingStatus(status) {
  const s = (status || '').toLowerCase();
  return ['pending', 'inprogress', 'in progress', 'running'].includes(s);
}

// Available folders for OpenSearch loading
const AVAILABLE_FOLDERS = [
  'ABSENCE',
  'ACTIVITY',
  'AP',
  'ASSET',
  'ASSETANALYSIS',
  'CASHBOOK',
  'CANDIDATE',
  'CUSTOMERACTIVITY',
  'EMPLOYEE',
  'GLACCOUNTS',
  'GLJOURNAL',
  'GLTRANS',
  'GLTRIALBALANCE',
  'GM',
  'HR',
  'INVOICEMATCHING',
  'ITEM',
  'ITEMLOCATION',
  'LOS',
  'METADATA',
  'PO',
  'POINQUIRY',
  'PR',
  'REPORTS',
  'RESOURCE',
  'REQUISITION',
  'TRANSACTIONANALYSIS',
  'VENDOR',
  'VENDORAGREEMENT'
];

export default function LoadData(props) {
  const { credentials } = props.fetchInitialData;

  // OpenSearch load state
  const [selectedFolders, setSelectedFolders] = useState([]);
  const [loadAllFolders, setLoadAllFolders] = useState(false);
  const [loadingOpenSearch, setLoadingOpenSearch] = useState(false);
  const [error, setError] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  // Runs table state
  const [runsData, setRunsData] = useState([]);
  const [runsLoading, setRunsLoading] = useState(true);
  const [errorDialog, setErrorDialog] = useState(null);

  const systemTimezone = credentials?.systemTimezone || 'America/New_York';
  const loadCols = useOpenSearchLoadsTableCols(systemTimezone);

  // Fetch runs from DynamoDB (table: apix_{stage}_sys_opensearch_loads)
  const fetchRuns = useCallback(async (silent = false) => {
    try {
      if (!silent) setRunsLoading(true);
      const resp = await lambdaFetch(
        'dynamo/opensearch-loads',
        'get',
        credentials.user.accessToken,
        null,
        '',
        credentials
      );
      const items = parseRunsResponse(resp);
      const data = processRunsData(items);
      setRunsData(data);
    } catch (e) {
      console.error('Error fetching OpenSearch load runs:', e);
      setRunsData([]);
    } finally {
      if (!silent) setRunsLoading(false);
    }
  }, [credentials]);

  useEffect(() => {
    fetchRuns();
  }, [fetchRuns]);

  /** Fetch real-time status from Step Functions (not DynamoDB) for a single execution */
  const fetchExecutionStatus = useCallback(async (executionId) => {
    try {
      const resp = await lambdaFetch(
        'opensearch/load/status',
        'post',
        credentials.user.accessToken,
        JSON.stringify({ executionId }),
        '',
        credentials
      );
      return resp?.success ? resp.data : null;
    } catch (e) {
      console.warn('Status check failed for', executionId, e);
      return null;
    }
  }, [credentials]);

  /** Refresh: fetch from DynamoDB, then check Step Functions for pending runs; refetch DynamoDB if any completed */
  const handleRefresh = useCallback(async () => {
    setRunsLoading(true);
    try {
      const resp = await lambdaFetch(
        'dynamo/opensearch-loads',
        'get',
        credentials.user.accessToken,
        null,
        '',
        credentials
      );
      const items = parseRunsResponse(resp);
      let data = processRunsData(items);
      const pendingRuns = data.filter((r) => isPendingStatus(r.status));

      if (pendingRuns.length > 0) {
        const statusResults = await Promise.all(
          pendingRuns.map((r) => fetchExecutionStatus(r.executionId))
        );
        const statusByExecutionId = {};
        const failureDetailsByExecutionId = {};
        statusResults.forEach((r) => {
          if (r?.executionId && r?.completed) {
            statusByExecutionId[r.executionId] = r.status;
            if (r.failureDetails) failureDetailsByExecutionId[r.executionId] = r.failureDetails;
          }
        });
        const anyCompleted = Object.keys(statusByExecutionId).length > 0;
        if (anyCompleted) {
          const refetchResp = await lambdaFetch(
            'dynamo/opensearch-loads',
            'get',
            credentials.user.accessToken,
            null,
            '',
            credentials
          );
          const refetchItems = parseRunsResponse(refetchResp);
          data = processRunsData(refetchItems);
          data = data.map((row) => {
            const status = statusByExecutionId[row.executionId];
            const failureDetails = failureDetailsByExecutionId[row.executionId];
            const error = failureDetails
              ? [failureDetails.error, failureDetails.cause].filter(Boolean).join('\n\nCause:\n')
              : row.error;
            return status ? { ...row, status, error: error || row.error } : row;
          });
        }
      }
      setRunsData(data);
    } catch (e) {
      console.error('Error refreshing OpenSearch load runs:', e);
      setRunsData([]);
    } finally {
      setRunsLoading(false);
    }
  }, [credentials, fetchExecutionStatus]);

  // Start OpenSearch load
  const handleStartOpenSearchLoad = async () => {
    setLoadingOpenSearch(true);
    setError(null);

    try {
      const requestBody = {
        folders: loadAllFolders ? [] : selectedFolders,
        loadAll: loadAllFolders
      };

      const response = await lambdaFetch(
        'opensearch/load',
        'post',
        credentials.user.accessToken,
        JSON.stringify(requestBody),
        'OpenSearch load started successfully',
        credentials
      );

      if (response && response.success) {
        setSnackbarMessage('OpenSearch load started. Click the refresh button to see status updates.');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
        fetchRuns();
      } else {
        throw new Error('Failed to start OpenSearch load');
      }
    } catch (err) {
      console.error('Error starting OpenSearch load:', err);
      setSnackbarMessage(`Failed to start OpenSearch load: ${err.message}`);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setLoadingOpenSearch(false);
    }
  };

  // Build table data with status chips and error links
  const tableData = runsData.map((row) => ({
    ...row,
    error_link: row.error ? (
      <Box
        component="span"
        className="editLink"
        sx={{ cursor: 'pointer', color: 'error.main' }}
        onClick={() => setErrorDialog({ executionId: row.executionId, error: row.error })}
      >
        view
      </Box>
    ) : (
      '—'
    ),
  }));

  return (
    <Box sx={{ p: 3 }}>
      <Paper elevation={2} sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
        <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
          Load Dropdowns
        </Typography>

        <Divider sx={{ mb: 3 }} />

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Trigger form */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Load TSV data from S3 into OpenSearch indices. You can select specific folders or load all
            folders. This operation runs asynchronously and may take several minutes to complete.
          </Typography>

          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <input
                type="checkbox"
                id="loadAllFolders"
                checked={loadAllFolders}
                onChange={(e) => {
                  setLoadAllFolders(e.target.checked);
                  if (e.target.checked) setSelectedFolders([]);
                }}
                disabled={loadingOpenSearch}
                style={{ marginRight: 8 }}
              />
              <label htmlFor="loadAllFolders">
                <Typography variant="body1">Load All Folders</Typography>
              </label>
            </Box>
          </Box>

          {!loadAllFolders && (
            <Autocomplete
              multiple
              fullWidth
              value={selectedFolders}
              onChange={(event, newValue) => setSelectedFolders(newValue)}
              options={AVAILABLE_FOLDERS}
              disabled={loadingOpenSearch}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Folders to Load"
                  placeholder="Select one or more folders..."
                  helperText={`${selectedFolders.length} folder(s) selected`}
                />
              )}
              sx={{ mb: 3 }}
              ListboxProps={{ style: { maxHeight: 300 } }}
            />
          )}

          <Button
            variant="contained"
            startIcon={loadingOpenSearch ? <CircularProgress size={20} /> : <PlayArrowIcon />}
            onClick={handleStartOpenSearchLoad}
            disabled={loadingOpenSearch || (!loadAllFolders && selectedFolders.length === 0)}
            sx={{ mb: 2 }}
          >
            {loadingOpenSearch ? 'Starting...' : 'Start OpenSearch Load'}
          </Button>

          <Box sx={{ mt: 2 }}>
            <Alert severity="info" sx={{ fontSize: '0.875rem' }}>
              Runs are tracked in the Load History table below. The refresh button fetches real-time status from Step Functions for pending runs. Hover over "View results" to see load statistics.
            </Alert>
          </Box>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Runs table */}
        <Typography variant="h6" gutterBottom>
          Load History
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Previous OpenSearch load runs and their statuses.
        </Typography>
        <Box
          sx={{
            margin:
              credentials?.appWidth < smallScreenWidth ? '1rem auto' : '1rem 0',
          }}
        >
          <Grid container>
            <Grid item xs={12} style={{ position: 'relative' }}>
              <ReactTable
                cols={loadCols}
                data={tableData}
                isLoading={runsLoading}
                onRefresh={handleRefresh}
                tableKey="opensearch-loads-table"
              />
            </Grid>
          </Grid>
        </Box>
      </Paper>

      {/* Error detail dialog */}
      <Dialog
        fullWidth
        maxWidth="sm"
        open={!!errorDialog}
        onClose={() => setErrorDialog(null)}
        aria-labelledby="error-dialog-title"
      >
        <DialogTitle id="error-dialog-title">
          Error details {errorDialog && `- ${errorDialog.executionId}`}
        </DialogTitle>
        <DialogContent dividers>
          {errorDialog && (
            <Typography
              component="pre"
              sx={{
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                fontFamily: 'monospace',
                fontSize: '0.875rem',
              }}
            >
              {errorDialog.error}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setErrorDialog(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          variant="filled"
          sx={{ fontSize: '0.875rem' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}
